<template>
  <div class="">
    <div
      v-if="flexiblePackageCost > 0"
      class="flex justify-between items-center bg-white overflow-hidden mb-4 text-gray-800 border text-sm"
    >
      <div class="p-3 pl-4 pr-4 flex items-center leading-none">
        <i
          class="fas fa-info-circle text-gray-800 mr-2"
          style="margin-top: 1px"
        />
        <span v-if="!existingFlexibleMembership">
          <span class="mr-2 font-semibold">Recommended flexible package cost</span>
          We have set your flexible package price at £{{ flexiblePackageCost }} based on your full
          member price.
        </span>
        <span
          v-if="existingFlexibleMembership"
          class="ml-1"
        >
          <div class="mr-2 font-semibold mb-1">Recommended flexible package price</div>
          We have set your flexible package price at £{{ flexiblePackageCost }} based on the
          available cohorts for our platform.
        </span>
      </div>

      <div class="mr-1" />
    </div>

    <div class="flex justify-between items-baseline select-none mb-4">
      <div class="text-gray-700 text-lg">
        Results from 20,000 simulations
      </div>

      <div />
    </div>

    <div class="flex justify-between w-full min-w-full mb-2">
      <div class="w-7/12">
        <div class="">
          <div
            v-for="tab in tabs"
            :key="tab"
            class="p-1 pl-3 pr-3 border cursor-pointer inline-block mr-2 transition-all duration-500 select-none"
            :class="
              selectedTab === tab
                ? 'text-white  bg-secondary-100 border-secondary-100 '
                : 'text-gray-500 hover:text-gray-700 bg-white '
            "
            @click="selectTab(tab)"
          >
            {{ tab }}
          </div>
        </div>
      </div>

      <div class="inline-block">
        <div
          v-if="years > 1"
          class="flex items-center border text-gray-400 bg-white"
        >
          <div
            class="p-1 pl-3 pr-3 border-r cursor-pointer hover:text-gray-800 transition-all duration-500"
            @click="previousYear()"
          >
            <i class="fas fa-minus leading-none text-xs" />
          </div>
          <div class="leading-none pl-3 pr-3 text-gray-800">
            Year {{ yearIndex + 1 }}
          </div>
          <div
            class="p-1 pl-3 pr-3 border-l cursor-pointer hover:text-gray-800 transition-all duration-500"
            @click="nextYear()"
          >
            <i class="fas fa-plus leading-none text-xs" />
          </div>
        </div>
      </div>
    </div>

    <div class="flex w-full min-w-full mb-2">
      <div class="w-7/12" />

      <div class="w-5/12">
        <div
          class="p-4 text-center shadow bg-gradient-to-b from-pmg-500 to bg-pmg-100 font-medium text-white"
        >
          <div v-if="getProfitDifference() > 1000">
            <div class="leading-none text-2xl font-bold flex items-center justify-center">
              <i class="fas fa-long-arrow-up mr-1 text-xl" />
              {{ formatCurrency(getProfitDifference()) }}
            </div>
            <div class="text-sm font-normal mt-1">
              more average profit with PlayMoreGolf
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="relative flex items-center mb-8">
      <div class="w-8/12 flex border bg-white p-8 pl-0">
        <div class="border-r w-56 pr-10 text-right font-semibold text-gray-800">
          <div
            v-if="selectedTab === 'Summary'"
            class="h-4"
          />
          <div
            v-else
            class="h-12"
          />
          <div
            v-if="selectedTab === 'Summary'"
            class="py-1 font-normal"
          >
            <div
              class="inline-block tool"
              :data-tooltip="
                'The average values that were achieved for year ' +
                  (yearIndex + 1) +
                  ' from the 20,000 simulations.'
              "
            >
              <i class="fad fa-info-circle duration-250 text-sm transition-all cursor-pointer" />
            </div>
            <span
              v-if="years > 1"
              class="ml-1"
            >Year {{ yearIndex + 1 }}</span>
          </div>
          <div class="py-2">
            <div
              v-if="selectedTab === 'Summary'"
              class="flex items-center justify-end -mr-6 text-gray-800 hover:text-gray-600 cursor-pointer transition-all duration-500 select-none"
              @click="openAvgTotalCostBreakdown = !openAvgTotalCostBreakdown"
            >
              <div>Average cost</div>
              <div
                class="w-6 text-right text-xs"
                style="margin-top: 2px"
              >
                <i
                  v-if="!openAvgTotalCostBreakdown"
                  class="fas fa-plus"
                />
                <i
                  v-else
                  class="fas fa-minus"
                />
              </div>
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
              class="font-normal"
            >
              <div
                class="inline-block mr-1 w-2 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The price of your partnership fee with PlayMoreGolf.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Partnership fee
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
              class="font-normal"
            >
              <div
                class="inline-block mr-1 w-2 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="getSoftwareTooltip()"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Software cost
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
              class="font-normal"
            >
              <div
                class="inline-block mr-1 w-2 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The average total amount of fees charged for processing card transactions.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Card fees
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
              class="font-normal flex items-center justify-end -mr-6 text-gray-800 hover:text-gray-600 cursor-pointer transition-all duration-500 select-none"
              @click="openPayrollBreakdown = !openPayrollBreakdown"
            >
              <div>Payroll</div>
              <span
                class="w-6 text-right text-xs"
                style="margin-top: 2px"
              >
                <i
                  v-if="!openPayrollBreakdown"
                  class="fas fa-plus"
                />
                <i
                  v-else
                  class="fas fa-minus"
                />
              </span>
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown"
              class="font-normal text-gray-500"
            >
              <div
                class="inline-block mr-1 w-2 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The payroll costs associated with providing a customer service.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Customer services
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown"
              class="font-normal text-gray-500"
            >
              <div
                class="inline-block mr-1 w-2 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The payroll costs associated with renewing your current members.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Renewals
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown"
              class="font-normal text-gray-500"
            >
              <div
                class="inline-block mr-1 w-2 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The payroll costs associated with onboarding new members.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Onboarding
            </div>

            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
              class="font-normal"
            >
              <div
                class="inline-block mr-1 w-2 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="'Your total yearly cost on social media including paid adverts/boosted posts and agency costs.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Digital Marketing
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
              class="font-normal"
            >
              <div
                class="inline-block mr-1 w-2 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="'Your total yearly cost for all other marketing activities including printed adverts, design work etc.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Other marketing
            </div>

            <div
              v-if="selectedTab === 'Fixed Costs'"
              class="text-gray-500 font-normal"
            >
              <div
                class="inline-block mr-1 w-3 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The maximum total sum of license/partnership fees, marketing spend, onboarding cost, renewal cost and customer service cost.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Maximum
            </div>
            <div
              v-if="selectedTab === 'Profit'"
              class="text-gray-500 font-normal"
            >
              <div
                class="inline-block mr-1 w-3 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The maximum profit value that was achieved out of the 20,000 simulations.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Maximum
            </div>
            <div
              v-if="selectedTab === 'ROI'"
              class="text-gray-500 font-normal"
            >
              <div
                class="inline-block mr-1 w-3 text-sm text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The maximum ROI value that was achieved out of the 20,000 simulations.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Maximum
            </div>
          </div>
          <div class="py-2">
            <div
              v-if="selectedTab === 'Summary'"
              class="flex items-center justify-end -mr-6 text-gray-800 hover:text-gray-600 cursor-pointer transition-all duration-500 select-none"
              @click="openAvgTotalRevenueBreakdown = !openAvgTotalRevenueBreakdown"
            >
              <div>Average revenue</div>
              <div
                class="w-6 text-right text-xs"
                style="margin-top: 2px"
              >
                <i
                  v-if="!openAvgTotalRevenueBreakdown"
                  class="fas fa-plus"
                />
                <i
                  v-else
                  class="fas fa-minus"
                />
              </div>
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown"
              class="font-normal"
            >
              <div
                class="inline-block mr-1 w-2 text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The total average revenue received from retained members over the next 12 months(after commission for PMG)'"
              >
                <i
                  class="fad fa-info-circle text-sm text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Member retention
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown"
              class="font-normal"
            >
              <div
                class="inline-block mr-1 w-2 text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The total average revenue received from membership growth over the next 12 months(after commission for PMG)'"
              >
                <i
                  class="fad fa-info-circle text-sm text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Member growth
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown"
              class="font-normal"
            >
              <div
                class="inline-block mr-1 w-2 text-center"
                style="z-index: 20 !important"
                :data-tooltip="'The total amount of flexi revenue received from PlayMoreGolf members over the next 12 months'"
              >
                <i
                  class="fad fa-info-circle text-sm text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Flexi revenue
            </div>

            <div
              v-if="selectedTab === 'Fixed Costs'"
              class="text-xl"
            >
              <div
                class="inline-block mr-1 w-4 text-base text-center font-normal"
                style="z-index: 15 !important"
                :data-tooltip="'The average total sum of license/partnership fees, marketing spend, onboarding cost, renewal cost and customer service cost.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Average
            </div>
            <div
              v-if="selectedTab === 'Profit'"
              class="text-xl"
            >
              <div
                class="inline-block mr-1 w-4 text-base text-center font-normal"
                style="z-index: 15 !important"
                :data-tooltip="'The average profit value that was achieved from the 20,000 simulations.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Average
            </div>
            <div
              v-if="selectedTab === 'ROI'"
              class="text-xl"
            >
              <div
                class="inline-block mr-1 w-4 text-base text-center font-normal"
                style="z-index: 15 !important"
                :data-tooltip="'The average ROI value that was achieved from the 20,000 simulations.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Average
            </div>
          </div>
          <div class="py-2">
            <div v-if="selectedTab === 'Summary'">
              Average profit
            </div>
            <div
              v-if="selectedTab === 'Fixed Costs'"
              class="text-gray-500 font-normal"
            >
              <div
                class="inline-block mr-1 w-3 text-sm text-center"
                style="z-index: 10 !important"
                :data-tooltip="'The minimum total sum of license/partnership fees, marketing spend, onboarding cost, renewal cost and customer service cost.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Minimum
            </div>
            <div
              v-if="selectedTab === 'Profit'"
              class="text-gray-500 font-normal"
            >
              <div
                class="inline-block mr-1 w-5 text-center"
                style="z-index: 10 !important"
                :data-tooltip="'The minimum profit value that was achieved out of the 20,000 simulations.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Minimum
            </div>
            <div
              v-if="selectedTab === 'ROI'"
              class="text-gray-500 font-normal"
            >
              <div
                class="inline-block mr-1 w-5 text-center"
                style="z-index: 10 !important"
                :data-tooltip="'The minimum ROI value that was achieved out of the 20,000 simulations.'"
              >
                <i
                  class="fad fa-info-circle text-gray-600 hover:text-gray-800 duration-500 transition-all cursor-pointer"
                />
              </div>
              Minimum
            </div>
          </div>
          <div
            v-if="
              selectedTab === 'Profit' && comparisonResults.Product1.ProjectedProfit.RiskOfLoss > 0
            "
            class="py-2 font-normal text-gray-500"
          >
            <div>Risk of loss</div>
          </div>
        </div>

        <div class="text-center pl-20 ml-4 text-gray-500">
          <div>
            <div
              class="h-12 flex items-center justify-center text-xl font-bold heading text-gray-800"
            >
              Your own
            </div>
            <div class="py-2">
              <div
                v-if="selectedTab === 'Summary'"
                class="flex items-center justify-center"
              >
                <div>{{ formatCurrency(comparisonResults.Product1.AvgProjectedTotalCosts) }}</div>
              </div>
              <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                {{ formatCurrency(0) }}
              </div>
              <div
                v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
                style="margin-top: 2px"
              >
                {{ formatCurrency(comparisonResults.Product1.Fee) }}
              </div>
              <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                {{ formatCurrency(comparisonResults.Product1.AvgProjectedCardFees) }}
              </div>
              <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                {{
                  formatCurrency(
                    comparisonResults.Product1.AvgProjectedCustServCost +
                      comparisonResults.Product1.AvgProjectedRenewalCost +
                      comparisonResults.Product1.AvgProjectedSalesOnboardingCost
                  )
                }}
              </div>
              <div
                v-if="
                  selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown
                "
                style="margin-top: 2px"
              >
                {{ formatCurrency(comparisonResults.Product1.AvgProjectedCustServCost) }}
              </div>
              <div
                v-if="
                  selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown
                "
              >
                {{ formatCurrency(comparisonResults.Product1.AvgProjectedRenewalCost) }}
              </div>
              <div
                v-if="
                  selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown
                "
              >
                {{ formatCurrency(comparisonResults.Product1.AvgProjectedSalesOnboardingCost) }}
              </div>

              <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                {{ formatCurrency(comparisonResults.Product1.MarketingCost) }}
              </div>
              <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
                {{ formatCurrency(comparisonResults.Product1.SocialCost) }}
              </div>

              <div v-if="selectedTab === 'Fixed Costs'">
                {{ formatCurrency(comparisonResults.Product1.ProjectedFixedCosts.Max) }}
              </div>
              <div
                v-if="selectedTab === 'Profit'"
                :class="getTextColor(comparisonResults.Product1.ProjectedProfit.Max)"
              >
                {{ formatCurrency(comparisonResults.Product1.ProjectedProfit.Max) }}
              </div>
              <div
                v-if="selectedTab === 'ROI'"
                :class="getTextColor(comparisonResults.Product1.ProjectedROI.Max * 100)"
              >
                {{ formatPercentage(comparisonResults.Product1.ProjectedROI.Max * 100) }}
              </div>
            </div>
            <div class="py-2">
              <div
                v-if="selectedTab === 'Summary'"
                class="flex items-center justify-center"
              >
                <div>
                  {{ formatCurrency(comparisonResults.Product1.AvgProjectedNYTotalRevenue) }}
                </div>
              </div>
              <div
                v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown"
                style="margin-top: 2px"
              >
                {{ formatCurrency(comparisonResults.Product1.AvgProjectedNYRetention) }}
              </div>
              <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
                {{ formatCurrency(comparisonResults.Product1.AvgProjectedNYGrowth) }}
              </div>
              <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
                {{ formatCurrency(comparisonResults.Product1.AvgProjectedNYAwayRevenue) }}
              </div>

              <div
                v-if="selectedTab === 'Fixed Costs'"
                class="text-xl font-semibold text-gray-800"
              >
                {{ formatCurrency(comparisonResults.Product1.ProjectedFixedCosts.Mean) }}
              </div>
              <div
                v-if="selectedTab === 'Profit'"
                class="text-xl font-semibold text-gray-800"
                :class="getTextColor(comparisonResults.Product1.ProjectedProfit.Mean)"
              >
                {{ formatCurrency(comparisonResults.Product1.ProjectedProfit.Mean) }}
              </div>
              <div
                v-if="selectedTab === 'ROI'"
                class="text-xl font-semibold text-gray-800"
                :class="getTextColor(comparisonResults.Product1.ProjectedROI.Mean * 100)"
              >
                {{ formatPercentage(comparisonResults.Product1.ProjectedROI.Mean * 100) }}
              </div>
            </div>
            <div class="py-2">
              <div
                v-if="selectedTab === 'Summary'"
                :class="getTextColor(comparisonResults.Product1.ProjectedProfit.Mean)"
              >
                {{ formatCurrency(comparisonResults.Product1.ProjectedProfit.Mean) }}
              </div>
              <div v-if="selectedTab === 'Fixed Costs'">
                {{ formatCurrency(comparisonResults.Product1.ProjectedFixedCosts.Min) }}
              </div>
              <div
                v-if="selectedTab === 'Profit'"
                :class="getTextColor(comparisonResults.Product1.ProjectedProfit.Min)"
              >
                {{ formatCurrency(comparisonResults.Product1.ProjectedProfit.Min) }}
              </div>
              <div
                v-if="selectedTab === 'ROI'"
                :class="getTextColor(comparisonResults.Product1.ProjectedROI.Min * 100)"
              >
                {{ formatPercentage(comparisonResults.Product1.ProjectedROI.Min * 100) }}
              </div>
            </div>
            <div
              v-if="
                selectedTab === 'Profit' &&
                  comparisonResults.Product1.ProjectedProfit.RiskOfLoss > 0
              "
              class="py-2 text-red-500"
            >
              <div>
                {{ formatPercentage(comparisonResults.Product1.ProjectedProfit.RiskOfLoss) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Space filler -->
      <div class="w-4/12 select-none opacity-0">
        <div class="p-8 pb-0">
          <div
            class="mb-8 mt-5 flex items-center justify-center"
            style="padding-bottom: 7px"
          >
            <div>
              <div class="flex">
                <div class="w-7 h-7" />
                <div class="w-7 h-7 bg-pmg-400" />
                <div class="w-7 h-7" />
              </div>
              <div class="flex">
                <div class="w-7 h-7 bg-pmg-400" />
                <div class="w-7 h-7 bg-pmg-800" />
                <div class="w-7 h-7 bg-pmg-100" />
              </div>
              <div class="flex">
                <div class="w-7 h-7" />
                <div class="w-7 h-7 bg-pmg-100" />
                <div class="w-7 h-7" />
              </div>
            </div>
          </div>
          <div class="py-2">
            <div
              v-if="selectedTab === 'Summary'"
              class="flex items-center justify-center"
            >
              <div class="font-semibold">
                {{ formatCurrency(comparisonResults.Product2.AvgProjectedTotalCosts) }}
              </div>
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
              {{ formatCurrency(comparisonResults.Product2.Fee) }}
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
              style="margin-top: 2px"
            >
              {{ formatCurrency(0) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedCardFees) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
              {{
                formatCurrency(
                  comparisonResults.Product2.AvgProjectedCustServCost +
                    comparisonResults.Product2.AvgProjectedRenewalCost +
                    comparisonResults.Product2.AvgProjectedSalesOnboardingCost +
                    comparisonResults.Product2.MarketingCost +
                    comparisonResults.Product2.SocialCost
                )
              }}
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown"
              style="margin-top: 2px"
            >
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedCustServCost) }}
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown"
            >
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedRenewalCost) }}
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown"
            >
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedSalesOnboardingCost) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
              {{ formatCurrency(comparisonResults.Product2.MarketingCost) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
              {{ formatCurrency(comparisonResults.Product2.SocialCost) }}
            </div>

            <div v-if="selectedTab === 'Fixed Costs'">
              {{ formatCurrency(comparisonResults.Product2.ProjectedFixedCosts.Max) }}
            </div>
            <div v-if="selectedTab === 'Profit'">
              {{ formatCurrency(comparisonResults.Product2.ProjectedProfit.Max) }}
            </div>
            <div v-if="selectedTab === 'ROI'">
              {{ formatPercentage(comparisonResults.Product2.ProjectedROI.Max * 100) }}
            </div>
          </div>
          <div class="py-2">
            <div
              v-if="selectedTab === 'Summary'"
              class="flex items-center justify-center"
            >
              <div class="font-semibold">
                {{ formatCurrency(comparisonResults.Product2.AvgProjectedNYTotalRevenue) }}
              </div>
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedNYRetention) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedNYGrowth) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedNYAwayRevenue) }}
            </div>

            <div
              v-if="selectedTab === 'Fixed Costs'"
              class="text-xl"
            >
              {{ formatCurrency(comparisonResults.Product2.ProjectedFixedCosts.Mean) }}
            </div>
            <div
              v-if="selectedTab === 'Profit'"
              class="text-xl"
            >
              {{ formatCurrency(comparisonResults.Product2.ProjectedProfit.Mean) }}
            </div>
            <div
              v-if="selectedTab === 'ROI'"
              class="text-xl"
            >
              {{ formatPercentage(comparisonResults.Product2.ProjectedROI.Mean * 100) }}
            </div>
          </div>
          <div class="py-2">
            <div
              v-if="selectedTab === 'Summary'"
              class="font-semibold"
            >
              {{ formatCurrency(comparisonResults.Product2.ProjectedProfit.Mean) }}
            </div>
            <div v-if="selectedTab === 'Fixed Costs'">
              {{ formatCurrency(comparisonResults.Product2.ProjectedFixedCosts.Min) }}
            </div>
            <div v-if="selectedTab === 'Profit'">
              {{ formatCurrency(comparisonResults.Product2.ProjectedProfit.Min) }}
            </div>
            <div v-if="selectedTab === 'ROI'">
              {{ formatPercentage(comparisonResults.Product2.ProjectedROI.Min * 100) }}
            </div>
          </div>
          <div
            v-if="
              selectedTab === 'Profit' && comparisonResults.Product1.ProjectedProfit.RiskOfLoss > 0
            "
            class="py-2"
          >
            <div v-if="comparisonResults.Product2.ProjectedProfit.RiskOfLoss !== 0.0">
              {{ formatPercentage(comparisonResults.Product2.ProjectedProfit.RiskOfLoss) }}
            </div>
            <div v-else>
              0%
            </div>
          </div>
        </div>

        <div class="p-4">
          <a :href="'mailto:?subject=PlayMoreGolf membership platform simulator&body=' + link">
            <div
              class="mb-2 uppercase text-sm font-medium cursor-pointer leading-none transition-all duration-500 border text-gray-600 hover:text-gray-800 select-none p-4"
              style="background-color: #f9fafb"
            >
              <i class="fas fa-envelope leading-none mr-2" />
              Share your results
            </div>
          </a>
          <a
            href="https://www.playmore.golf/club-partner/"
            target="_blank"
          >
            <div
              class="uppercase text-sm font-medium cursor-pointer transition-all duration-500 leading-none hover:bg-secondary-200 bg-secondary-100 select-none text-white p-4"
            >
              <i class="fas fa-flag mr-2" />
              Become a club partner
            </div>
          </a>
        </div>
      </div>

      <div
        class="absolute top-0 right-0 bg-white shadow-xl border text-center text-gray-800 w-5/12"
      >
        <div class="p-8 pb-0">
          <div
            class="mb-8 mt-5 flex items-center justify-center"
            style="padding-bottom: 7px"
          >
            <div>
              <div class="flex">
                <div class="w-7 h-7" />
                <div class="w-7 h-7 bg-pmg-400" />
                <div class="w-7 h-7" />
              </div>
              <div class="flex">
                <div class="w-7 h-7 bg-pmg-400" />
                <div class="w-7 h-7 bg-pmg-800" />
                <div class="w-7 h-7 bg-pmg-100" />
              </div>
              <div class="flex">
                <div class="w-7 h-7" />
                <div class="w-7 h-7 bg-pmg-100" />
                <div class="w-7 h-7" />
              </div>
            </div>
          </div>
          <div class="py-2">
            <div
              v-if="selectedTab === 'Summary'"
              class="flex items-center justify-center"
            >
              <div class="font-semibold">
                {{ formatCurrency(comparisonResults.Product2.AvgProjectedTotalCosts) }}
              </div>
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
              {{ formatCurrency(comparisonResults.Product2.Fee) }}
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown"
              style="margin-top: 2px"
            >
              {{ formatCurrency(0) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedCardFees) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
              {{
                formatCurrency(
                  comparisonResults.Product2.AvgProjectedCustServCost +
                    comparisonResults.Product2.AvgProjectedRenewalCost +
                    comparisonResults.Product2.AvgProjectedSalesOnboardingCost +
                    comparisonResults.Product2.MarketingCost +
                    comparisonResults.Product2.SocialCost
                )
              }}
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown"
              style="margin-top: 2px"
            >
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedCustServCost) }}
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown"
            >
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedRenewalCost) }}
            </div>
            <div
              v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown && openPayrollBreakdown"
            >
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedSalesOnboardingCost) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
              {{ formatCurrency(comparisonResults.Product2.MarketingCost) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalCostBreakdown">
              {{ formatCurrency(comparisonResults.Product2.SocialCost) }}
            </div>

            <div v-if="selectedTab === 'Fixed Costs'">
              {{ formatCurrency(comparisonResults.Product2.ProjectedFixedCosts.Max) }}
            </div>
            <div v-if="selectedTab === 'Profit'">
              {{ formatCurrency(comparisonResults.Product2.ProjectedProfit.Max) }}
            </div>
            <div v-if="selectedTab === 'ROI'">
              {{ formatPercentage(comparisonResults.Product2.ProjectedROI.Max * 100) }}
            </div>
          </div>
          <div class="py-2">
            <div
              v-if="selectedTab === 'Summary'"
              class="flex items-center justify-center"
            >
              <div class="font-semibold">
                {{ formatCurrency(comparisonResults.Product2.AvgProjectedNYTotalRevenue) }}
              </div>
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedNYRetention) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedNYGrowth) }}
            </div>
            <div v-if="selectedTab === 'Summary' && openAvgTotalRevenueBreakdown">
              {{ formatCurrency(comparisonResults.Product2.AvgProjectedNYAwayRevenue) }}
            </div>

            <div
              v-if="selectedTab === 'Fixed Costs'"
              class="text-xl"
            >
              {{ formatCurrency(comparisonResults.Product2.ProjectedFixedCosts.Mean) }}
            </div>
            <div
              v-if="selectedTab === 'Profit'"
              class="text-xl"
            >
              {{ formatCurrency(comparisonResults.Product2.ProjectedProfit.Mean) }}
            </div>
            <div
              v-if="selectedTab === 'ROI'"
              class="text-xl"
            >
              {{ formatPercentage(comparisonResults.Product2.ProjectedROI.Mean * 100) }}
            </div>
          </div>
          <div class="py-2">
            <div
              v-if="selectedTab === 'Summary'"
              class="font-semibold"
            >
              {{ formatCurrency(comparisonResults.Product2.ProjectedProfit.Mean) }}
            </div>
            <div v-if="selectedTab === 'Fixed Costs'">
              {{ formatCurrency(comparisonResults.Product2.ProjectedFixedCosts.Min) }}
            </div>
            <div v-if="selectedTab === 'Profit'">
              {{ formatCurrency(comparisonResults.Product2.ProjectedProfit.Min) }}
            </div>
            <div v-if="selectedTab === 'ROI'">
              {{ formatPercentage(comparisonResults.Product2.ProjectedROI.Min * 100) }}
            </div>
          </div>
          <div
            v-if="
              selectedTab === 'Profit' && comparisonResults.Product1.ProjectedProfit.RiskOfLoss > 0
            "
            class="py-2"
          >
            <div v-if="comparisonResults.Product2.ProjectedProfit.RiskOfLoss !== 0.0">
              {{ formatPercentage(comparisonResults.Product2.ProjectedProfit.RiskOfLoss) }}
            </div>
            <div v-else>
              0%
            </div>
          </div>
        </div>

        <div class="p-4">
          <a :href="'mailto:?subject=PlayMoreGolf membership platform simulator&body=' + link">
            <div
              class="mb-2 uppercase text-sm font-medium cursor-pointer leading-none transition-all duration-500 border text-gray-600 hover:text-gray-800 select-none p-4"
              style="background-color: #f9fafb"
            >
              <i class="fas fa-envelope leading-none mr-2" />
              Share your results
            </div>
          </a>
          <a
            href="https://www.playmore.golf/club-partner/"
            target="_blank"
          >
            <div
              class="uppercase text-sm font-medium cursor-pointer transition-all duration-500 leading-none hover:bg-secondary-200 bg-secondary-100 select-none text-white p-4"
            >
              <i class="fas fa-flag mr-2" />
              Become a club partner
            </div>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    components: {},

    props: {
      comparisonResults: {
        type: Object,
        default: null,
      },
      yearIndex: {
        type: Number,
        default: 0,
      },
      years: {
        type: Number,
        default: 1,
      },
      flexiblePackageCost: {
        type: Number,
        default: 0,
      },
      link: {
        type: String,
        default: '',
      },
      selectedTabProp: {
        type: String,
        default: 'Summary',
      },
    },

    data() {
      return {
        selectedTab: 'Summary',
        tabs: ['Summary', 'Revenue', 'Fixed Costs', 'Profit', 'ROI'],

        openAvgTotalCostBreakdown: false,
        openPayrollBreakdown: false,

        openAvgTotalRevenueBreakdown: false,
      };
    },

    computed: {
      existingFlexibleMembership() {
        return this.$route.params.existingFlexibleMembership === 'true';
      },
    },

    mounted() {
      this.selectedTab = this.selectedTabProp;
    },

    methods: {
      getProfitDifference() {
        return (
          this.comparisonResults.Product2.ProjectedProfit.Mean
          - this.comparisonResults.Product1.ProjectedProfit.Mean
        );
      },

      getSoftwareTooltip() {
        if (this.existingFlexibleMembership) {
          return 'The total cost for the software you currently use to manage your own flexible membership.';
        }

        return 'The total cost for software to manage your own flexible membership.';
      },

      getMaximumText() {
        return 'The maximum value that was achieved out of the 20,000 simulations.';
      },

      getAverageText() {
        return 'The average value that was achieved from the 20,000 simulations.';
      },

      getMinimumText() {
        return 'The minimum value that was achieved out of the 20,000 simulations.';
      },

      formatCurrency(value) {
        return value
          .toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })
          .split('.')[0];
      },

      getTextColor(value) {
        if (Math.sign(value) === -1) {
          return 'text-red-500';
        }

        return '';
      },

      formatPercentage(value) {
        return `${value.toFixed(2)}%`;
      },

      nextYear() {
        this.$emit('incrementYear');
      },

      previousYear() {
        this.$emit('decrementYear');
      },

      selectTab(tab) {
        if (tab !== this.selectedTab) {
          this.$emit('selectTab', tab);
          this.selectedTab = tab;
        }
      },
    },
  };
</script>
